import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import { getLangFromCookie, LANG_COOKIE } from "../../../../Utils/LangCookie";
import { useLoading } from "../../../GlobalFunction/LoadingOverlay";

const START_DELAY_MS = 500;

const getCurrentLang = () =>
  getLangFromCookie() || localStorage.getItem(LANG_COOKIE) || "ZH";

/*
 * Background image rotation switch.
 *
 * false = all images remain straight
 * true  = images receive a small random rotation
 */
const ENABLE_BACKGROUND_IMAGE_ROTATION = false;

/*
 * Temporary horizontal-jitter value used by the current positioning
 * helper.
 *
 * This will be replaced by the shared pixel-based jitter system when
 * createBackgroundPosition() is updated.
 */
const BACKGROUND_HORIZONTAL_JITTER_RATIO = 0.18;

/*
 * Shared randomized background layout.
 *
 * Every depth level uses the same anchor pool, vertical range,
 * randomized-lane rules, and responsive image gaps.
 *
 * Nine anchors are used because the largest current image batch
 * contains nine images.
 */
const BACKGROUND_SHARED_ANCHOR_COUNT = 9;

/*
 * Fixed seed used to generate the shared randomized anchor template.
 *
 * The positions look random but remain stable after React rerenders.
 */
const BACKGROUND_SHARED_LAYOUT_SEED = 4711;

/*
 * Available vertical area for every background depth level.
 */
const BACKGROUND_VERTICAL_MIN_PERCENT = 10;
const BACKGROUND_VERTICAL_MAX_PERCENT = 88;

/*
 * Each anchor receives a unique vertical lane.
 *
 * This value keeps the randomized position away from the exact top
 * and bottom edges of its assigned lane.
 *
 * 0.2 means the image may use the middle 60% of the lane.
 */
const BACKGROUND_VERTICAL_LANE_INSET_RATIO = 0.2;

/*
 * Minimum visible edge-to-edge gap between neighboring images.
 *
 * The same responsive gap values are used by Level 1, Level 2,
 * and Level 3 regardless of their image count or image size.
 */
const BACKGROUND_IMAGE_GAP = {
  xs: 56,
  sm: 72,
  md: 88,
  lg: 104,
};

/*
 * Maximum horizontal random movement inside an image slot.
 *
 * Later panel-width calculations will reserve this space on both
 * sides, so the movement cannot reduce the minimum image gap.
 */
const BACKGROUND_HORIZONTAL_JITTER_PX = {
  xs: 8,
  sm: 10,
  md: 12,
  lg: 14,
};

/*
 * Responsive keys used when calculating background-panel widths
 * and slot spacing.
 */
const BACKGROUND_RESPONSIVE_KEYS = [
  "xs",
  "sm",
  "md",
  "lg",
];

/*
 * Circle size controls.
 *
 * These scales enlarge the visible circles without changing their
 * absolute positions inside the circle stage.
 *
 * Outer default:
 *   controls the normal size of the three colored circles.
 *
 * Outer hover:
 *   is applied on top of the default scale when hovered.
 *
 * Center default:
 *   controls the white logo circle size.
 */
const OUTER_CIRCLE_BASE_TRANSFORM = {
  xs: "scale(1.08)",
  sm: "scale(1.10)",
  md: "scale(1.12)",
  lg: "scale(1.12)",
};

const OUTER_CIRCLE_HOVER_TRANSFORM = {
  xs: "scale(1.18)",
  sm: "scale(1.20)",
  md: "scale(1.25)",
  lg: "scale(1.25)",
};

const CENTER_CIRCLE_BASE_TRANSFORM = {
  xs: "scale(1.08)",
  sm: "scale(1.10)",
  md: "scale(1.12)",
  lg: "scale(1.12)",
};

const INTRODUCTION_COPY = {
  ZH: {
    top: {
      title: {
        ZH: "企業潔淨夥伴",
        EN: "Clean Partner",
        JP: "企業の衛生パートナー"
      },
      description: {
        ZH: "水禾擁有服務各大知名企業與廠商的豐富經驗，是您值得信賴的清潔衛生夥伴。",
        EN: "SEHO has extensive experience serving leading companies and manufacturers, making us your trusted hygiene partner.",
        JP: "水禾は大手企業やメーカーへの豊富なサービス経験を持つ、信頼できる衛生パートナーです。"
      },
    },

    left: {
      title: {
        ZH: "精準成本管理",
        EN: "Cost Control",
        JP: "精密なコスト管理"
      },
      description: {
        ZH: "根據不同產業需求，量身規劃最適合的環衛方案，確保品質及有效降低不必要的支出。",
        EN: "We tailor hygiene solutions to each industry’s needs, ensuring quality while reducing unnecessary costs.",
        JP: "業種ごとのニーズに合わせた衛生管理プランを提案し、品質を確保しながら不要なコストを削減します。"
      },
    },

    right: {
      title: {
        ZH: "高效環衛應變",
        EN: "Fast Hygiene Response",
        JP: "迅速な衛生対応"
      },
      description: {
        ZH: "水禾能快速因應客戶需求，並及時提供解決方案，協助企業打造安全高效的工作環境。",
        EN: "SEHO responds quickly to customer needs and provides timely solutions to help businesses build safer, more efficient workplaces.",
        JP: "水禾はお客様のニーズに迅速に対応し、最適な解決策を提供して、安全で効率的な職場環境づくりを支援します。"
      },
    },
  },
};

/*
 * Introduction background image batches.
 *
 * Level 1 and Level 2 intentionally use different image lists.
 * Level 3 intentionally reuses the exact Level 1 image list.
 */
const LEVEL_1_BACKGROUND_IMAGES = [
  {
    src: "/img/LandingPage/b2b/intro-1.webp",
    alt: "企業服務規劃",
  },
  {
    src: "/img/LandingPage/b2b/intro-3.webp",
    alt: "現場衛生管理",
  },
  {
    src: "/img/LandingPage/b2b/intro-5.webp",
    alt: "企業合作服務",
  },
  {
    src: "/img/LandingPage/b2b/intro-7.webp",
    alt: "企業團隊合作",
  },
  
];

const LEVEL_2_BACKGROUND_IMAGES = [
  {
    src: "/img/products/B2B/area-preview/clean.webp",
    alt: "企業潔淨服務",
  },
  {
    src:  "/img/products/B2B/area-preview/cook.webp",
    alt: "客戶服務支援",
  },
  {
    src:  "/img/products/B2B/area-preview/fstore.webp",
    alt: "衛生管理規劃",
  },
  {
    src:  "/img/products/B2B/area-preview/handwash.webp",
    alt: "衛生管理規劃",
  },
  {
    src:  "/img/products/B2B/area-preview/locker.webp",
    alt: "衛生管理規劃",
  },
  {
    src:  "/img/products/B2B/area-preview/office.webp",
    alt: "衛生管理規劃",
  },
  {
    src:  "/img/products/B2B/area-preview/restroom.webp",
    alt: "衛生管理規劃",
  },
   {
    src:  "/img/products/B2B/area-preview/pack.webp",
    alt: "衛生管理規劃",
  },
   {
    src:  "/img/products/B2B/area-preview/pre.webp",
    alt: "衛生管理規劃",
  },
];

/*
 * Level 3 uses the same image references as Level 1.
 *
 * Do not spread or duplicate this array. Keeping the same reference
 * makes the intended Level 1/Level 3 relationship explicit.
 */
const LEVEL_3_BACKGROUND_IMAGES =
  LEVEL_1_BACKGROUND_IMAGES;

/*
 * The background is divided into three independent moving depth
 * layers. Images are not arranged in straight rows. Their top
 * positions are generated separately and remain fixed while the
 * layers move horizontally.
 */
const BACKGROUND_DEPTH_LEVELS = [
  {
    id: "far",
    zIndex: 1,

    imageBatch:
      LEVEL_1_BACKGROUND_IMAGES,

    imageWidth: {
      xs: 135,
      sm: 165,
      md: 190,
      lg: 210,
    },
    blur: 9,
    opacity: 0.3,
    whiteOverlay: 0.12,
    duration: 54,
    animationDelay: -19,
  },

  {
    id: "middle",
    zIndex: 2,

    imageBatch:
      LEVEL_2_BACKGROUND_IMAGES,

    imageWidth: {
      xs: 200,
      sm: 250,
      md: 285,
      lg: 310,
    },

    blur: 3.5,
    opacity: 0.5,
    whiteOverlay: 0.16,
    duration: 42,
    animationDelay: -11,
  },

  {
    id: "front",
    zIndex: 3,

    imageBatch:
      LEVEL_3_BACKGROUND_IMAGES,

    /*
     * Level 3 uses the same approximate size as Level 2.
     */
    imageWidth: {
      xs: 200,
      sm: 250,
      md: 285,
      lg: 310,
    },
    blur: 0,
    opacity: 0.8,
    whiteOverlay: 0,
    duration: 34,
    animationDelay: -6,
  },
];

/*
 * Generate repeatable pseudo-random values.
 *
 * Math.random() is intentionally not used because it would cause
 * images to jump to new heights whenever React rerenders.
 */
function seededUnit(seed) {
  const value =
    Math.sin(seed * 12.9898) *
    43758.5453;

  return value - Math.floor(value);
}

/**
 * Return a deterministically shuffled copy of an array.
 *
 * The result looks random but remains identical after React
 * rerenders and page reloads.
 */
function seededShuffle(
  values,
  seed,
) {
  const shuffled = [
    ...values,
  ];

  for (
    let index =
      shuffled.length - 1;
    index > 0;
    index -= 1
  ) {
    const randomValue =
      seededUnit(
        seed +
          index * 97,
      );

    const swapIndex =
      Math.floor(
        randomValue *
          (index + 1),
      );

    [
      shuffled[index],
      shuffled[swapIndex],
    ] = [
      shuffled[swapIndex],
      shuffled[index],
    ];
  }

  return shuffled;
}

/**
 * Build one shared randomized anchor template.
 *
 * Horizontal order remains evenly spaced from left to right.
 * Vertical lanes are shuffled so neighboring images do not follow
 * a rigid top-to-bottom pattern.
 *
 * Every vertical lane is used only once, preventing two anchors from
 * receiving the same vertical band.
 */
function createSharedBackgroundAnchors() {
  const verticalLanes =
    seededShuffle(
      Array.from(
        {
          length:
            BACKGROUND_SHARED_ANCHOR_COUNT,
        },
        (_, index) => index,
      ),
      BACKGROUND_SHARED_LAYOUT_SEED,
    );

  return Array.from(
    {
      length:
        BACKGROUND_SHARED_ANCHOR_COUNT,
    },
    (_, anchorIndex) => ({
      anchorIndex,

      /*
       * Horizontal positions remain in sequential slots so their
       * minimum gaps can be guaranteed later.
       */
      horizontalSlotIndex:
        anchorIndex,

      /*
       * The vertical lanes are randomly reordered, but no lane is
       * repeated inside the shared template.
       */
      verticalLaneIndex:
        verticalLanes[
          anchorIndex
        ],

      /*
       * These repeatable random values add a small variation inside
       * each anchor's safe area.
       */
      horizontalRandom:
        seededUnit(
          BACKGROUND_SHARED_LAYOUT_SEED +
            1000 +
            anchorIndex * 41,
        ),

      verticalRandom:
        seededUnit(
          BACKGROUND_SHARED_LAYOUT_SEED +
            2000 +
            anchorIndex * 59,
        ),

      rotationRandom:
        seededUnit(
          BACKGROUND_SHARED_LAYOUT_SEED +
            3000 +
            anchorIndex * 73,
        ),
    }),
  );
}

/*
 * One shared anchor array is used by all three depth levels.
 *
 * Level 1 and Level 3 will later select the same four evenly spread
 * anchors. Level 2 will use all nine anchors.
 */
const SHARED_BACKGROUND_ANCHORS =
  createSharedBackgroundAnchors();

/**
 * Select anchors evenly across the complete shared layout.
 *
 * Examples:
 *
 * Nine images:
 * → use all nine shared anchors
 *
 * Four images:
 * → select randomized anchor data from approximately
 *   anchor 1, anchor 4, anchor 6, and anchor 9
 *
 * The horizontal slot remains a precise fractional value, so the
 * visible horizontal gaps stay equal even when the selected source
 * anchor index has to be rounded.
 */
function selectSharedBackgroundAnchors(
  imageCount,
) {
  const totalAnchorCount =
    SHARED_BACKGROUND_ANCHORS.length;

  const safeImageCount = Math.min(
    Math.max(
      Math.trunc(
        Number(imageCount) || 0,
      ),
      0,
    ),
    totalAnchorCount,
  );

  if (safeImageCount === 0) {
    return [];
  }

  /*
   * Place a single image in the middle of the shared layout.
   */
  if (safeImageCount === 1) {
    const middleSlotIndex =
      (totalAnchorCount - 1) / 2;

    const sourceAnchorIndex =
      Math.round(middleSlotIndex);

    return [
      {
        ...SHARED_BACKGROUND_ANCHORS[
          sourceAnchorIndex
        ],

        horizontalSlotIndex:
          middleSlotIndex,
      },
    ];
  }

  const lastSharedSlotIndex =
    totalAnchorCount - 1;

  return Array.from(
    {
      length: safeImageCount,
    },
    (_, imageIndex) => {
      /*
       * Keep the horizontal positions exactly evenly spaced across
       * the complete shared nine-slot layout.
       */
      const evenlySpacedSlotIndex =
        (
          imageIndex *
          lastSharedSlotIndex
        ) /
        (safeImageCount - 1);

      /*
       * Use the closest shared anchor for its randomized vertical
       * lane and repeatable random values.
       */
      const sourceAnchorIndex =
        Math.round(
          evenlySpacedSlotIndex,
        );

      const sourceAnchor =
        SHARED_BACKGROUND_ANCHORS[
          sourceAnchorIndex
        ];

      return {
        ...sourceAnchor,

        /*
         * Preserve the exact fractional horizontal position rather
         * than the rounded source-anchor slot.
         */
        horizontalSlotIndex:
          evenlySpacedSlotIndex,
      };
    },
  );
}

/**
 * Safely read one responsive numeric value.
 */
function getResponsiveBackgroundValue(
  values,
  breakpoint,
) {
  const directValue = Number(
    values?.[breakpoint],
  );

  if (Number.isFinite(directValue)) {
    return directValue;
  }

  const fallbackValue = Number(
    values?.lg ??
      values?.md ??
      values?.sm ??
      values?.xs ??
      0,
  );

  return Number.isFinite(fallbackValue)
    ? fallbackValue
    : 0;
}

/**
 * Calculate one level's responsive horizontal layout.
 *
 * Every level uses the same minimum edge-to-edge gap.
 *
 * Two jitter spaces are reserved inside every slot:
 * - one for the image on the left;
 * - one for the image on the right.
 *
 * This means two neighboring images may move toward one another
 * without reducing the configured minimum gap.
 */
function createBackgroundLayoutMetrics(
  imageWidth,
) {
  const slotPitch = {};
  const panelWidth = {};

  BACKGROUND_RESPONSIVE_KEYS.forEach(
    (breakpoint) => {
      const responsiveImageWidth =
        getResponsiveBackgroundValue(
          imageWidth,
          breakpoint,
        );

      const responsiveGap =
        getResponsiveBackgroundValue(
          BACKGROUND_IMAGE_GAP,
          breakpoint,
        );

      const responsiveJitter =
        getResponsiveBackgroundValue(
          BACKGROUND_HORIZONTAL_JITTER_PX,
          breakpoint,
        );

      /*
       * Minimum center-to-center distance:
       *
       * image width
       * + required visible gap
       * + reserved jitter on both sides
       */
      const responsiveSlotPitch =
        responsiveImageWidth +
        responsiveGap +
        responsiveJitter * 2;

      slotPitch[breakpoint] =
        responsiveSlotPitch;

      /*
       * Every level uses the same number of shared layout slots,
       * regardless of how many unique images exist in its batch.
       */
      panelWidth[breakpoint] =
        responsiveSlotPitch *
        BACKGROUND_SHARED_ANCHOR_COUNT;
    },
  );

  return {
    slotPitch,
    panelWidth,
  };
}

function createBackgroundPosition(levelIndex, imageIndex, imageCount) {
  const safeCount = Math.max(imageCount, 1);

  const slotWidth = 100 / safeCount;

  const horizontalRandom = seededUnit(
    (levelIndex + 1) * 101 + (imageIndex + 1) * 37,
  );

  const verticalRandom = seededUnit(
    (levelIndex + 1) * 211 + (imageIndex + 1) * 53,
  );

  const rotationRandom = seededUnit(
    (levelIndex + 1) * 307 + (imageIndex + 1) * 71,
  );

  /*
   * Keep every image close to the center of its assigned horizontal
   * slot so neighboring images maintain a wider visible gap.
   *
   * A small amount of variation remains to avoid a perfectly rigid
   * grid.
   */
  const horizontalJitter =
    (horizontalRandom - 0.5) *
    slotWidth *
    BACKGROUND_HORIZONTAL_JITTER_RATIO;

  const left =
    imageIndex * slotWidth +
    slotWidth / 2 +
    horizontalJitter;

  /*
   * Images appear at random-looking heights between 10% and 88%
   * of the section instead of sharing one row.
   */
  const top = 10 + verticalRandom * 78;

  const rotation = ENABLE_BACKGROUND_IMAGE_ROTATION //for tilting the bg images
    ? -2.2 + rotationRandom * 4.4
    : 0;

  return {
    left: `${left}%`,
    top: `${top}%`,
    rotation: `${rotation}deg`,
  };
}

const CIRCLE_CONFIG = [
  {
    id: "top",
    backgroundColor: "#005F91",

    position: {
      top: -30,
      left: "50%",
      transform: "translateX(-50%)",
    },

    /*
     * The white circle is below this circle.
     * Anchor the bottom edge so expansion goes upward.
     */
    activeTransformOrigin: "50% 100%",

    entranceDelay: 450,

    activePadding: {
      xs: "13%",
      sm: "14%",
      md: "14%",
    },
  },

  {
    id: "left",
    backgroundColor: "#438FC7",

    /*
     * The white circle is near the top-right side.
     * Anchor that corner so expansion goes down and left.
     */
    activeTransformOrigin: "100% 0%",

    position: {
      /*
       * These coordinates account for the enlarged base scale.
       * They maintain a small gap between all three outer circles.
       */
      top: {
        xs: 150,
        sm: 220,
        md: 241,
        lg: 269,
      },

      left: {
        xs: 11,
        sm: 18,
        md: 30,
        lg: 19,
      },
    },

    entranceDelay: 800,

    activePadding: {
      xs: "13%",
      sm: "14%",
      md: "14%",
    },
  },

  {
    id: "right",
    backgroundColor: "#35A5AA",

    /*
     * The white circle is near the top-left side.
     * Anchor that corner so expansion goes down and right.
     */
    activeTransformOrigin: "0% 0%",

    position: {
      top: {
        xs: 150,
        sm: 220,
        md: 241,
        lg: 269,
      },

      right: {
        xs: 11,
        sm: 18,
        md: 30,
        lg: 19,
      },
    },

    entranceDelay: 1150,

    activePadding: {
      xs: "13%",
      sm: "14%",
      md: "14%",
    },
  },
];

function normalizeLanguage(lang) {
  const normalized = String(lang || "ZH")
    .trim()
    .toUpperCase();

  return ["ZH", "EN", "JP"].includes(normalized) ? normalized : "ZH";
}

function getLocalizedIntroductionText(value, lang) {
  if (!value) {
    return "";
  }

  if (typeof value === "string") {
    return value;
  }

  return value?.[lang] || value?.ZH || "";
}

function BackgroundDepthImage({ image, level, position }) {
  return (
    <Box
      sx={{
        position: "absolute",

        left: position.left,
        top: position.top,

        width: level.imageWidth,
        aspectRatio: "16 / 9",

        transform: [
          "translate(-50%, -50%)",
          `rotate(${position.rotation})`,
        ].join(" "),

        overflow: "hidden",

        opacity: level.opacity,

        filter: level.blur > 0 ? `blur(${level.blur}px)` : "none",

        boxShadow:
          level.id === "front" ? "0 16px 40px rgba(0, 0, 0, 0.08)" : "none",

        pointerEvents: "none",
        userSelect: "none",
      }}
    >
      <Box
        component="img"
        src={image.src}
        alt=""
        draggable={false}
        loading="eager"
        decoding="async"
        sx={{
          display: "block",

          width: "100%",
          height: "100%",

          objectFit: "cover",

          userSelect: "none",
          pointerEvents: "none",
        }}
      />

      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          inset: 0,

          backgroundColor: `rgba(255, 255, 255, ${level.whiteOverlay})`,

          pointerEvents: "none",
        }}
      />
    </Box>
  );
}

export default function IntroductionSection({
  lang,
  fonts = {},
  prefersReducedMotion = false,

  logoSrc = "/img/Logo/LOGO-05.webp",
}) {
  const { isLoading } = useLoading();

  const sectionRef = useRef(null);

  const [hasEntered, setHasEntered] = useState(false);

  const [animationStarted, setAnimationStarted] = useState(false);

  const [activeCircle, setActiveCircle] = useState(null);

  const [currentLang, setCurrentLang] = useState(() =>
    normalizeLanguage(lang || getCurrentLang()),
  );

  useEffect(() => {
    setCurrentLang(normalizeLanguage(lang || getCurrentLang()));
  }, [lang]);

  useEffect(() => {
    const syncLanguage = () => {
      setCurrentLang(normalizeLanguage(getCurrentLang()));
    };

    window.addEventListener("app:languageChanged", syncLanguage);
    window.addEventListener("storage", syncLanguage);

    return () => {
      window.removeEventListener("app:languageChanged", syncLanguage);
      window.removeEventListener("storage", syncLanguage);
    };
  }, []);

  const normalizedLang = currentLang;

  const copy = INTRODUCTION_COPY[normalizedLang] || INTRODUCTION_COPY.ZH;

  const fontFamily = fonts?.[normalizedLang] || "LINE Seed TW";

  /*
   * Prepare every depth level using its own assigned image batch.
   *
   * The image count passed to createBackgroundPosition() belongs to
   * that specific level, so the horizontal slots and spacing are
   * calculated independently for the three-image and four-image
   * batches.
   *
   * Both copies of each moving panel reuse the same prepared items,
   * allowing the marquee loop to connect without a visible jump.
   */
  const preparedBackgroundLevels = useMemo(
    () =>
      BACKGROUND_DEPTH_LEVELS.map(
        (level, levelIndex) => {
          const validImages =
            Array.isArray(
              level.imageBatch,
            )
              ? level.imageBatch.filter(
                  (image) =>
                    image &&
                    typeof image.src ===
                      "string" &&
                    image.src.trim() !==
                      "",
                )
              : [];

          /*
           * All configured levels currently contain valid images.
           * Level 1 is retained as a safe local fallback in case a
           * level is accidentally configured with an empty batch.
           */
          const levelImages =
            validImages.length > 0
              ? validImages
              : LEVEL_1_BACKGROUND_IMAGES;

          const layoutMetrics =
            createBackgroundLayoutMetrics(
              level.imageWidth,
            );

          return {
            ...level,
            ...layoutMetrics,

            items: levelImages.map(
              (
                image,
                imageIndex,
              ) => ({
                image,

                position:
                  createBackgroundPosition(
                    levelIndex,
                    imageIndex,
                    levelImages.length,
                  ),
              }),
            ),
          };
        },
      ),
    [],
  );

  /*
   * Start the sequence only when this section enters the screen
   * for the first time.
   */
  useEffect(() => {
    const section = sectionRef.current;

    if (isLoading || !section || hasEntered) {
      return undefined;
    }

    if (typeof IntersectionObserver === "undefined") {
      setHasEntered(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) {
          return;
        }

        setHasEntered(true);
        observer.disconnect();
      },
      {
        threshold: 0.2,
        rootMargin: "0px 0px -10% 0px",
      },
    );

    observer.observe(section);

    return () => {
      observer.disconnect();
    };
  }, [hasEntered, isLoading]);

  /*
   * Keep the background clear for exactly three seconds after
   * the section first enters the viewport.
   *
   * The blur and circle sequence begin together after the delay.
   */
  useEffect(() => {
    if (isLoading || !hasEntered) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setAnimationStarted(true);
    }, START_DELAY_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [hasEntered, isLoading]);

  /*
   * Clear a touch-selected circle when the user clicks outside
   * the complete Introduction section.
   */
  useEffect(() => {
    const handlePointerDown = (event) => {
      const section = sectionRef.current;

      if (!section || section.contains(event.target)) {
        return;
      }

      setActiveCircle(null);
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setActiveCircle(null);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);

      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const handleCircleClick = (event, circleID) => {
    event.stopPropagation();

    setActiveCircle((current) => (current === circleID ? null : circleID));
  };

  const handleCircleKeyDown = (event, circleID) => {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }

    event.preventDefault();

    setActiveCircle((current) => (current === circleID ? null : circleID));
  };

  const circleSize = {
    xs: 150,
    sm: 205,
    md: 250,
    lg: 270,
  };

  const centerCircleSize = {
    xs: 92,
    sm: 118,
    md: 142,
    lg: 170,
  };

  return (
    <Box
      ref={sectionRef}
      component="section"
      aria-label="SEHO introduction"
      onClick={() => setActiveCircle(null)}
      sx={{
        position: "relative",
        mt: {xs: 1, md: 7, lg: 10},
        mb: {xs: 1, md: 7, lg: 10},

        width: "100%",

        minHeight: {
          xs: 590,
          sm: 690,
          md: 760,
          lg: 780,
        },

        overflow: "hidden",
        isolation: "isolate",

        backgroundColor: "#fffffc",

        display: "flex",
        alignItems: "center",
        justifyContent: "center",

        py: {
          xs: 4,
          sm: 4.5,
          md: 5,
        },

        "@keyframes sehoIntroductionMarquee": {
          "0%": {
            transform: "translate3d(0, 0, 0)",
          },

          "100%": {
            transform: "translate3d(-50%, 0, 0)",
          },
        },
      }}
    >
      {/* =====================================================
          THREE SCATTERED BACKGROUND DEPTH LAYERS
      ====================================================== */}
      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          inset: 0,

          zIndex: 1,

          overflow: "hidden",

          pointerEvents: "none",
        }}
      >
        {preparedBackgroundLevels.map((level) => (
          <Box
            key={level.id}
            sx={{
              position: "absolute",
              inset: 0,

              zIndex: level.zIndex,

              overflow: "hidden",

              pointerEvents: "none",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,

                display: "flex",

                width: "max-content",
                height: "100%",

                animation: prefersReducedMotion
                  ? "none"
                  : [
                      "sehoIntroductionMarquee",
                      `${level.duration}s`,
                      "linear",
                      "infinite",
                    ].join(" "),

                animationDelay: prefersReducedMotion
                  ? "0s"
                  : `${level.animationDelay}s`,

                willChange: "transform",
              }}
            >
              {[0, 1].map((panelIndex) => (
                <Box
                  key={`${level.id}-${panelIndex}`}
                  sx={{
                    position: "relative",

                    flex: "0 0 auto",

                    width: level.panelWidth,
                    height: "100%",
                  }}
                >
                  {level.items.map(({ image, position }, imageIndex) => (
                    <BackgroundDepthImage
                      key={[level.id, panelIndex, image.src, imageIndex].join(
                        "-",
                      )}
                      image={image}
                      level={level}
                      position={position}
                    />
                  ))}
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Box>

      {/* Keep the screenshot's soft white atmosphere. */}
      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          inset: 0,

          zIndex: 2,

          background: "rgba(255, 255, 252, 0.05)",

          pointerEvents: "none",
        }}
      />

      {/* =====================================================
          FOUR-CIRCLE STAGE
      ====================================================== */}
      <Box
        sx={{
          position: "relative",
          zIndex: 4,

          /*
           * Move the complete four-circle formation downward
           * without changing the positions between the circles.
           */
          transform: {
            xs: "translateY(18px)",
            sm: "translateY(24px)",
            md: "translateY(30px)",
            lg: "translateY(34px)",
          },

          width: {
            xs: 340,
            sm: 490,
            md: 600,
            lg: 640,
          },

          height: {
            xs: 300,
            sm: 440,
            md: 520,
            lg: 555,
          },

          maxWidth: "100%",
          flexShrink: 0,
        }}
      >
        {CIRCLE_CONFIG.map((circle) => {
          const circleCopy = copy[circle.id];

          const isActive = activeCircle === circle.id;

          return (
            <Box
              key={circle.id}
              role="button"
              tabIndex={animationStarted ? 0 : -1}
              aria-expanded={isActive}
              aria-label={circleCopy?.title || ""}
              onMouseEnter={() => {
                if (animationStarted) {
                  setActiveCircle(circle.id);
                }
              }}
              onMouseLeave={() => {
                setActiveCircle((current) =>
                  current === circle.id ? null : current,
                );
              }}
              onFocus={() => {
                if (animationStarted) {
                  setActiveCircle(circle.id);
                }
              }}
              onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) {
                  setActiveCircle((current) =>
                    current === circle.id ? null : current,
                  );
                }
              }}
              onClick={(event) => handleCircleClick(event, circle.id)}
              onKeyDown={(event) => handleCircleKeyDown(event, circle.id)}
              sx={{
                position: "absolute",

                width: circleSize,
                height: circleSize,

                ...circle.position,

                zIndex: isActive ? 8 : 4,

                outline: "none",

                WebkitTapHighlightColor: "transparent",

                pointerEvents: animationStarted ? "auto" : "none",

                cursor: animationStarted ? "pointer" : "default",

                "&:focus-visible > .seho-introduction-entrance > .seho-introduction-circle":
                  {
                    outline: "4px solid rgba(255,255,255,.9)",

                    outlineOffset: 5,
                  },
              }}
            >
              {/* Entrance animation layer */}
              <Box
                className="seho-introduction-entrance"
                sx={{
                  width: "100%",
                  height: "100%",

                  opacity: animationStarted ? 1 : 0,

                  transform: animationStarted
                    ? OUTER_CIRCLE_BASE_TRANSFORM
                    : "scale(0.06)",

                  transition: prefersReducedMotion
                    ? "none"
                    : [
                        "opacity 700ms ease",
                        "transform 850ms cubic-bezier(.2,.85,.25,1.15)",
                      ].join(", "),

                  transitionDelay: prefersReducedMotion
                    ? "0ms"
                    : `${circle.entranceDelay}ms`,

                  transformOrigin: "center center",

                  willChange: "opacity, transform",
                }}
              >
                {/* Hover/touch expansion layer */}
                <Box
                  className="seho-introduction-circle"
                  sx={{
                    width: "100%",
                    height: "100%",

                    borderRadius: "50%",

                    backgroundColor: circle.backgroundColor,

                    color: "#ffffff",

                    boxShadow: isActive
                      ? "none"
                      : "0 12px 32px rgba(0, 61, 94, 0.12)",

                    transform: isActive
                      ? OUTER_CIRCLE_HOVER_TRANSFORM
                      : "scale(1)",

                    transition: prefersReducedMotion
                      ? "none"
                      : [
                          "transform 520ms cubic-bezier(.2,.85,.25,1.12)",
                          "box-shadow 420ms ease",
                        ].join(", "),

                    transformOrigin:
                      circle.activeTransformOrigin || "center center",

                    overflow: "hidden",

                    display: "flex",
                    flexDirection: "column",

                    alignItems: "center",

                    justifyContent: "center",

                    textAlign: "center",

                    padding: isActive
                      ? circle.activePadding
                      : {
                          xs: 2.5,
                          sm: 4,
                          md: 5,
                        },

                    boxSizing: "border-box",

                    userSelect: "none",
                  }}
                >
                  <Box
                    component="h2"
                    sx={{
                      width: "100%",

                      m: 0,

                      fontFamily,
                      fontWeight: 700,

                      fontSize: {
                        xs: isActive ? 15 : 17,

                        sm: isActive ? 19 : 22,

                        md: isActive ? 21 : 24,

                        lg: isActive ? 22 : 26,
                      },

                      lineHeight: 1.35,

                      letterSpacing: {
                        xs: 0,
                        sm: 0.4,
                        md: 0.6,
                      },

                      textAlign: "center",

                      transition: prefersReducedMotion
                        ? "none"
                        : ["font-size 350ms ease", "transform 350ms ease"].join(
                            ", ",
                          ),
                    }}
                  >
                    {getLocalizedIntroductionText(
                      circleCopy?.title,
                      normalizedLang,
                    )}
                  </Box>

                  <Box
                    component="p"
                    sx={{
                      width: "100%",
                      maxWidth: {
                        xs: "94%",
                        sm: "92%",
                        md: "90%",
                      },

                      m: 0,
                      mx: "auto",

                      mt: isActive
                        ? {
                            xs: 1.2,
                            sm: 1.6,
                            md: 1.8,
                          }
                        : 0,

                      maxHeight: isActive
                        ? {
                            xs: 260,
                            sm: 300,
                            md: 340,
                          }
                        : 0,

                      opacity: isActive ? 1 : 0,

                      overflow: "hidden",

                      fontFamily,
                      fontWeight: 400,

                      fontSize: {
                        xs: 9,
                        sm: 13,
                        md: 13,
                        lg: 13,
                      },

                      lineHeight: {
                        xs: 1.65,
                        sm: 1.75,
                        md: 1.5,
                      },

                      letterSpacing: {
                        xs: 0.1,
                        sm: 0.2,
                      },

                      overflowWrap: "anywhere",
                      wordBreak: "normal",

                      textAlign: "center",

                      transition: prefersReducedMotion
                        ? "none"
                        : [
                            "opacity 320ms ease 120ms",
                            "max-height 520ms ease",
                            "margin 320ms ease",
                          ].join(", "),
                    }}
                  >
                    {getLocalizedIntroductionText(
                      circleCopy?.description,
                      normalizedLang,
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          );
        })}

        {/* =================================================
            CENTER WHITE LOGO CIRCLE
        ================================================== */}
        <Box
          aria-hidden="true"
          sx={{
            position: "absolute",

            top: {
              xs: "48.5%",
              sm: "49%",
              md: "49%",
            },

            left: "50%",

            width: centerCircleSize,
            height: centerCircleSize,

            transform: "translate(-50%, -50%)",

            zIndex: 20,

            pointerEvents: "none",
          }}
        >
          <Box
            sx={{
              width: "100%",
              height: "100%",

              opacity: animationStarted ? 1 : 0,

              transform: animationStarted
                ? CENTER_CIRCLE_BASE_TRANSFORM
                : "scale(0.03)",

              transition: prefersReducedMotion
                ? "none"
                : [
                    "opacity 650ms ease",
                    "transform 900ms cubic-bezier(.16,.95,.25,1.18)",
                  ].join(", "),

              transformOrigin: "center center",

              willChange: "opacity, transform",
            }}
          >
            <Box
              sx={{
                width: "100%",
                height: "100%",

                borderRadius: "50%",

                backgroundColor: "#fffffc",

                display: "flex",
                alignItems: "center",
                justifyContent: "center",

                boxShadow: [
                  "0 5px 13px rgba(0,0,0,.22)",
                  "0 18px 42px rgba(0,0,0,.12)",
                ].join(", "),
              }}
            >
              <Box
                component="img"
                src={logoSrc}
                alt=""
                draggable={false}
                sx={{
                  display: "block",

                  width: {
                    xs: "67%",
                    sm: "68%",
                    md: "69%",
                  },

                  maxHeight: "58%",

                  objectFit: "contain",

                  userSelect: "none",
                  pointerEvents: "none",
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
